import React from 'react';
import { Link } from 'react-router-dom';
import FitScoreLogo from '../components/FitScoreLogo';
import { useScrollAnimations } from '../ScrollAnimations';
import ladderImage from '../assets/ladder-wall.jpg';
import psychologyImage from '../assets/psychology-brain.jpg';
import '../ScrollAnimations.css';
import './Home.css';

function Home() {
  useScrollAnimations();
  
  return (
    <div className="home">
      {/* Hero Section */}
      <section className="hero">
        {/* Background video */}
        <video autoPlay loop muted playsInline className="hero-video">
          <source src="/workshop-video.mp4" type="video/mp4" />
        </video>
        <div className="hero-overlay"></div>
        
        <div className="container hero-content">
          <p className="welcome-text">Welcome to</p>
          <div className="hero-logo-container">
            <div className="logo-wrapper">
              {/* Pulsing rings */}
              <div className="pulse-ring pulse-ring-1"></div>
              <div className="pulse-ring pulse-ring-2"></div>
              <div className="pulse-ring pulse-ring-3"></div>
              
              {/* Rotating dots */}
              <div className="dot-container">
                <div className="pulse-dot dot-1"></div>
                <div className="pulse-dot dot-2"></div>
                <div className="pulse-dot dot-3"></div>
                <div className="pulse-dot dot-4"></div>
              </div>
              
              {/* Logo - static, no pulse */}
              <img src="/fit-logo.jpg" alt="F-IT Score Logo" className="hero-logo-image" />
            </div>
          </div>
          <p className="hero-tagline-top">Born from lived experience, built on psychology, driven by passion</p>
          <h1 className="hero-title">Start Anywhere, Scale Everywhere</h1>
          <p className="hero-tagline-bottom">The missing link between Wellbeing & Workplace Safety</p>
        </div>
      </section>

      {/* Origin Story */}
      <section className="origin-story" style={{backgroundImage: `url(${ladderImage})`}}>
        <div className="container">
          <h2 className="section-title fade-in-up">The Origin Story</h2>
          <div className="story-content fade-in-left">
            <p><span className="fit-inline"><span className="f-orange">F</span>-IT</span> Score was created through a conversation in 2017 between Jason Anker MBE and Prof. Tim Marsh as they were discussing why Jason had chosen to work unsafely and ignore his safety concerns on the day of his workplace accident in 1993.</p>
            
            <p className="quote-highlight">"What had exactly gone through your mind as you were stood at the bottom of&nbsp;the&nbsp;untied&nbsp;ladder?"</p>
            
            <p>Tim asked.</p>
            
            <p>Jason explained how he was feeling at the time - doing a job he hated, struggling with marriage problems, drowning in debt. He knew the job was unsafe. He knew he should speak up but in that moment, feeling as low as he did, the answer was simple:</p>
            
            <p className="quote-response scale-in">"F**k it, get up the ladder and get the job done"</p>
            
            <p>That answer was the beginning of the <span className="fit-inline"><span className="f-orange">F</span>-IT</span> journey.</p>
          </div>
        </div>
      </section>

      {/* Journey Section */}
      <section className="journey-section" style={{backgroundImage: `url(${ladderImage})`}}>
        <div className="container">
          <h2 className="section-title fade-in-up">The 25-Year Journey</h2>
          <div className="journey-content">
            <div className="journey-timeline">
              <div className="timeline-item stagger-item fade-in-up">
                <div className="timeline-year">1993</div>
                <div className="timeline-content">
                  <h3>The Accident</h3>
                  <p>Jason's workplace accident changed everything. Standing at the bottom of an untied ladder, knowing it was unsafe, he made a choice that would alter his life forever.</p>
                  <p>The ripple effect was devastating - impacting not just Jason, but his family, friends, and everyone around him.</p>
                </div>
              </div>

              <div className="timeline-item stagger-item fade-in-up">
                <div className="timeline-year">1993-2018</div>
                <div className="timeline-content">
                  <h3>The Struggle</h3>
                  <p>For 25 years, Jason lived with the consequences of that moment. The physical injuries, the emotional toll, the impact on relationships - all stemming from one decision made when his wellbeing was compromised.</p>
                  <p>Looking back, it became clear: <strong>poor wellbeing at the time of the accident had been the main contributory reason why he chose not to speak up about his safety concerns.</strong></p>
                </div>
              </div>

              <div className="timeline-item highlight-timeline stagger-item fade-in-up">
                <div className="timeline-year">2018</div>
                <div className="timeline-content">
                  <h3>The Turning Point</h3>
                  <p>Twenty-five years after the accident, Jason finally started to turn his life around. Through understanding his own journey, he developed a framework that could help others avoid the same fate.</p>
                  <p>This became the foundation of <span className="fit-inline"><span className="f-orange">F</span>-IT</span> Score - a way to measure and understand wellbeing before it leads to critical decisions.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="benefits">
        <div className="container">
          <h2 className="section-title fade-in-up">Why Companies Choose <span className="fit-inline"><span className="f-orange">F</span>-IT</span></h2>
          <div className="benefits-grid">
            <div className="benefit-item fade-in-left">
              <div className="benefit-number">01</div>
              <h3>Start Anywhere, Scale Everywhere</h3>
              <p>Whether you begin with a powerful presentation, hands-on workshop, or the digital app - every entry point leads to the same authentic journey. Choose what works for your organisation today, and grow from there.</p>
            </div>
            <div className="benefit-item fade-in-up">
              <div className="benefit-number">02</div>
              <h3>A Journey, Not a Campaign</h3>
              <p>This isn't a temporary initiative that fades after six months. <span className="fit-inline"><span className="f-orange">F</span>-IT</span> is a permanent cultural shift - a language and framework that becomes part of how your people think, speak, and act every day.</p>
            </div>
            <div className="benefit-item fade-in-right">
              <div className="benefit-number">03</div>
              <h3>Real Change, Not Buzzwords</h3>
              <p>People are tired of corporate slogans that mean nothing. <span className="fit-inline"><span className="f-orange">F</span>-IT</span> works because it's honest, authentic, and connects to what employees already feel. It's not manufactured - it's real.</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta">
        <div className="container cta-content">
          <h2>Ready to Transform Your Approach to Wellbeing, Health & Safety?</h2>
          <p><span className="fit-inline-white"><span className="f-white">F</span>-IT</span> Score encompasses wellbeing, health & safety - helping companies understand the vital connection between how people feel and the decisions they make. When wellbeing suffers, safety suffers.</p>
          <div className="cta-buttons">
            <Link to="/demo" className="btn btn-white">Request a Demo</Link>
            <Link to="/contact" className="btn btn-outline-white">Contact Us</Link>
          </div>
        </div>
      </section>
    </div>
  );
}

export default Home;
